/**
 * Retry a function with exponential backoff
 * @param {Function} fn - The function to retry
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} - Promise that resolves with the function result or rejects after all retries
 */
export const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      return result;
    } catch (error) {
      lastError = error;

      // Don't retry authentication errors
      if (
        error.message &&
        (error.message.includes("Failed to authenticate") ||
          error.message.includes("Token expired") ||
          error.message.includes("Unauthorized") ||
          error.message.includes("Authentication"))
      ) {
        throw error;
      }

      // Don't retry on the last attempt
      if (attempt === maxRetries) {
        break;
      }

      // Calculate delay with exponential backoff and jitter
      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;

      console.warn(`Attempt ${attempt + 1} failed, retrying in ${Math.round(delay)}ms:`, error.message);

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

/**
 * Check if a network error is retryable
 * @param {Error} error - The error to check
 * @returns {boolean} - Whether the error is retryable
 */
export const isRetryableError = (error) => {
  if (!error) return false;

  const retryableMessages = [
    "NetworkError",
    "Failed to fetch",
    "Network request failed",
    "timeout",
    "ECONNRESET",
    "ENOTFOUND",
    "ECONNREFUSED",
  ];

  const message = error.message || "";
  return retryableMessages.some((msg) => message.includes(msg));
};
